import React from 'react';
import type { EvidenceFilters as Filters } from '../../types/evidence';
import { Filter, X } from 'lucide-react';

interface EvidenceFiltersProps {
  filters: Filters;
  onFilterChange: (filters: Filters) => void;
  onReset: () => void;
}

const EvidenceFilters: React.FC<EvidenceFiltersProps> = ({
  filters,
  onFilterChange,
  onReset
}) => {
  const handleChange = (key: keyof Filters, value: any) => {
    onFilterChange({ ...filters, [key]: value });
  };

  const hasActiveFilters = Object.values(filters).some(v => v !== undefined && v !== '');

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Filter className="h-5 w-5 text-gray-600" />
          <h3 className="text-sm font-semibold text-gray-900">Filters</h3>
        </div>
        {hasActiveFilters && (
          <button
            onClick={onReset}
            className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
          >
            <X className="h-4 w-4" />
            Clear All
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Status Filter */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Status
          </label>
          <select
            value={filters.status || ''}
            onChange={(e) => handleChange('status', e.target.value || undefined)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="syncing">Syncing</option>
            <option value="inactive">Inactive</option>
            <option value="error">Error</option>
            <option value="pending">Pending</option>
          </select>
        </div>

        {/* Source Type Filter */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Source Type
          </label>
          <select
            value={filters.sourceType || ''}
            onChange={(e) => handleChange('sourceType', e.target.value || undefined)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Sources</option>
            <option value="github">GitHub</option>
            <option value="gitlab">GitLab</option>
            <option value="ssl_labs">SSL Labs</option>
            <option value="aws_cloud">AWS Cloud</option>
            <option value="siem_splunk">Splunk SIEM</option>
            <option value="siem_elastic">Elasticsearch</option>
            <option value="manual_upload">Manual Upload</option>
          </select>
        </div>

        {/* Validation Status Filter */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Validation
          </label>
          <select
            value={filters.validationStatus || ''}
            onChange={(e) => handleChange('validationStatus', e.target.value || undefined)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Validation</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="needs_review">Needs Review</option>
          </select>
        </div>

        {/* Staleness Filter */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Staleness
          </label>
          <select
            value={filters.isStale === undefined ? '' : filters.isStale.toString()}
            onChange={(e) => handleChange('isStale', e.target.value === '' ? undefined : e.target.value === 'true')}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Items</option>
            <option value="false">Current Only</option>
            <option value="true">Stale/Expired</option>
          </select>
        </div>
      </div>

      {/* Active Filters Count */}
      {hasActiveFilters && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <p className="text-xs text-gray-600">
            {Object.values(filters).filter(v => v !== undefined && v !== '').length} filter(s) active
          </p>
        </div>
      )}
    </div>
  );
};

export default EvidenceFilters;
